import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import store from 'store';
import getDashboardApiUrl from '@/utils/getDashboardApiUrl';

const baseUrl = getDashboardApiUrl(window.location.href);

export interface ActivityActor {
	_id?: string;
	name: string;
	type: 'user' | 'customer' | 'system';
}

export interface ActivityTarget {
	_id?: string;
	name: string;
	type: 'customer' | 'user' | 'conversation';
}

export type ActivityType =
	| 'message_inbound'
	| 'message_outbound'
	| 'customer_created'
	| 'note'
	| 'assignment'
	| 'event'
	| 'reminder_completed';

export interface ActivityItem {
	_id: string;
	type: ActivityType;
	timestamp: string;
	_business: string;
	businessName?: string;
	_conversation?: string | null;
	_customer?: string | null;
	actor: ActivityActor;
	target?: ActivityTarget | null;
	content: string;
	channel?: string | null;
	_sourceId: string;
	_sourceType: 'Message' | 'TeamMessage' | 'Customer' | 'ActivityEvent';
	customerPhone?: string | null;
	customerEmail?: string | null;
}

export interface PaginationInfo {
	currentPage: number;
	totalPages: number;
	totalCount: number;
	hasNextPage: boolean;
	hasPrevPage: boolean;
	limit: number;
}

export interface ActivityFeedResponse {
	activities: ActivityItem[];
	pagination: PaginationInfo;
}

export interface ActivityFeedParams {
	page?: number;
	limit?: number;
	_user?: string;
	_business?: string;
	search?: string;
	types?: string;
}

export interface ActivityFeedUser {
	_id: string;
	name: string;
	username: string;
}

export interface ActivityFeedBusiness {
	_id: string;
	name: string;
}

export const activityFeedApi = createApi({
	reducerPath: 'activityFeedApi',
	baseQuery: fetchBaseQuery({
		baseUrl,
		prepareHeaders: (headers) => {
			const token = store.get('jwt');
			if (token) {
				headers.set('Authorization', `JWT ${token}`);
			}
			return headers;
		},
	}),
	endpoints: (builder) => ({
		getActivityFeed: builder.query<ActivityFeedResponse, ActivityFeedParams>({
			query: (params) => ({
				url: '/api/activity-feed',
				params,
			}),
			// Use serializeQueryArgs to create a stable cache key based on filters (not page)
			serializeQueryArgs: ({ queryArgs }) => {
				const { page, ...rest } = queryArgs;
				return rest;
			},
			// Merge new pages into existing cache for infinite scroll
			merge: (currentCache, newItems, { arg }) => {
				if (arg.page === 1) {
					// First page - replace cache
					return newItems;
				}
				// Subsequent pages - merge activities
				return {
					activities: [...currentCache.activities, ...newItems.activities],
					pagination: newItems.pagination,
				};
			},
			// Force refetch when args change
			forceRefetch: ({ currentArg, previousArg }) => {
				return currentArg?.page !== previousArg?.page;
			},
		}),
		getActivityFeedUsers: builder.query<{ users: ActivityFeedUser[] }, void>({
			query: () => '/api/activity-feed/users',
		}),
		getActivityFeedBusinesses: builder.query<
			{ businesses: ActivityFeedBusiness[]; allowAllLocations: boolean },
			void
		>({
			query: () => '/api/activity-feed/businesses',
		}),
	}),
});

export const {
	useGetActivityFeedQuery,
	useGetActivityFeedUsersQuery,
	useGetActivityFeedBusinessesQuery,
	useLazyGetActivityFeedQuery,
} = activityFeedApi;
